import { Dialog } from "@remo-co/ui-core/src/components/Dialog";
import { DialogContent } from "@remo-co/ui-core/src/components/DialogContent";
import { DialogTitle } from "@remo-co/ui-core/src/components/DialogTitle";
import { Divider } from "@remo-co/ui-core/src/components/Divider";
import { Box } from "@remo-co/ui-core/src/components/Box";
import { Button } from "@remo-co/ui-core/src/components/Button";
import { IconButtonWithTooltip } from "@remo-co/ui-core/src/components/IconButtonWithTooltip";
import { Typography } from "@remo-co/ui-core/src/components/Typography";
import { Close } from "@remo-co/ui-core/src/icons/Close";
import { ContactSupport } from "@remo-co/ui-core/src/icons/ContactSupport";
import { Call } from "@remo-co/ui-core/src/icons/Call";
import { Email } from "@remo-co/ui-core/src/icons/Email";
import { useI18n } from "i18n";
import { ContactChannelTypes, IContactChannel } from "modules/eventForm/types";
import { useSelector } from "react-redux";
import { selectUser } from "modules/auth/redux/selectors";
import useNotificationActions from "modules/notification/hooks/useNotificationActions";
import { Events, TRACKING_CONTEXT } from "modules/tracking";
import { useContext } from "react";
import { IEvent } from "modules/event/types";
import { useStyles } from "./styles";

interface Props {
  origin: string;
  companyName?: string;
  event: IEvent;
  close: () => void;
}

export const ContactHostDialog = ({
  origin,
  companyName,
  event,
  close,
}: Props) => {
  const { track } = useContext(TRACKING_CONTEXT);
  const { addSuccessNotification } = useNotificationActions();
  const user = useSelector(selectUser);
  const { t } = useI18n(["eventForm", "common"]);
  const styles = useStyles();

  const getChannelIcon = (type: ContactChannelTypes) => {
    switch (type) {
      case ContactChannelTypes.EMAIL:
        return <Email className={styles.icons} />;
      case ContactChannelTypes.PHONE:
        return <Call className={styles.icons} />;
      case ContactChannelTypes.OTHER:
      default:
        return <ContactSupport className={styles.icons} />;
    }
  };

  const handleCopyClick = (channel: IContactChannel) => {
    track(
      channel.type === ContactChannelTypes.PHONE
        ? Events.CONTACT_HOST_PHONE_CLICKED
        : Events.CONTACT_HOST_OTHER_CLICKED,
      {
        eventId: event?.id,
        url: origin,
        userId: user?.id,
      },
    );
    navigator.clipboard.writeText(channel.value);
    addSuccessNotification({
      message: t("common:contact.details.copied"),
    });
  };

  const handleEmailClick = () => {
    track(Events.CONTACT_HOST_EMAIL_CLICKED, {
      eventId: event?.id,
      url: origin,
      userId: user?.id,
    });
  };

  if (!event.contactChannels) {
    return null;
  }

  return (
    <Dialog
      data-testid="contact-host-dialog"
      onClose={close}
      aria-labelledby={t("common:contact.host")}
      fullWidth
      open
      classes={{
        paper: styles.root,
      }}
    >
      <DialogTitle className={styles.header}>
        <Typography variant="h3" className={styles.contactHostTitle}>
          {t("common:contact.host")}
        </Typography>
        <IconButtonWithTooltip
          id="close-contact-host-dialog"
          title={t("common:button.close")}
          data-testid="close-contact-host-dialog"
          onClick={close}
          size="small"
          className={styles.closeButton}
        >
          <Close />
        </IconButtonWithTooltip>
      </DialogTitle>
      <DialogContent className={styles.dialogContent}>
        <Box className={styles.channels}>
          <Typography variant="h4" className={styles.title}>
            {companyName || t("menu:unnamed.company")}
          </Typography>
          {event.contactChannels.map((channel) => (
            <Box
              display="flex"
              flexDirection="row"
              alignItems="center"
              justifyContent="space-between"
            >
              <Box display="flex" flexDirection="row" alignItems="center">
                {getChannelIcon(channel.type)}
                <Typography variant="body1">{channel.value}</Typography>
              </Box>
              {channel.type === ContactChannelTypes.EMAIL ? (
                <Button<"a">
                  onClick={handleEmailClick}
                  variant="secondary"
                  color="dark"
                  size="xs"
                  contentClassName={styles.actionButtonContent}
                  target="_blank"
                  rel="noreferrer"
                  href={`mailto:${channel.value}`}
                >
                  {t("common:email")}
                </Button>
              ) : (
                <Button
                  onClick={() => handleCopyClick(channel)}
                  variant="secondary"
                  color="dark"
                  size="xs"
                  contentClassName={styles.actionButtonContent}
                >
                  {t("common:button.copy")}
                </Button>
              )}
            </Box>
          ))}
        </Box>
        <Divider className={styles.divider} />
        <Box className={styles.details}>
          <Typography variant="body1" className={styles.detail}>
            <b>{t("common:event.name")}:</b> {event.name}
          </Typography>
          <Typography variant="body1" className={styles.detail}>
            <b>{t("common:user.email")}:</b>{" "}
            {user ? user.email : t("common:not.logged.in")}
          </Typography>
        </Box>
      </DialogContent>
    </Dialog>
  );
};
